import { flagsApi } from "./api";

const KEY = "foinn_flags";
const LABEL_KEY = "foinn_flag_labels";

// 4 named colours for everyone, then premium members add c5…c50 as they go
export const FLAG_ORDER = ["red", "orange", "green", "blue"];
export const FLAG_COLORS = {
  red: "#e11d48",
  orange: "#ff6b00",
  green: "#0f8a5f",
  blue: "#2563eb",
};
export const DEFAULT_LABELS = {
  red: "To learn",
  orange: "Almost there",
  green: "In my repertoire",
  blue: "For later",
};
export const MAX_FLAG_COLORS = 999; // founder + premium: unlimited in practice

// extra colours are derived from their index: same colour on every device
const hsl = (h, s, l) => {
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const v = l / 100 - (a / 100) * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.round(255 * v)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const flagHex = (color) => {
  if (!color) return null;
  if (FLAG_COLORS[color]) return FLAG_COLORS[color];
  const n = Number(String(color).slice(1));
  if (!n) return "#8a2be2";
  return hsl((n * 137.508) % 360, 68, 45);
};

export const flagRank = (color) =>
  FLAG_ORDER.includes(color) ? FLAG_ORDER.indexOf(color) + 1 : Number(String(color).slice(1)) || 99;

export const getFlags = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
};

const write = (list) => {
  localStorage.setItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new Event("flag-changed"));
};

export const getFlag = (key) => getFlags().find((f) => f.key === key);
export const flagColor = (key) => getFlag(key)?.color || null;

export const flagsForTune = (tuneId) =>
  getFlags().filter((f) => String(f.tune_id) === String(tuneId));

export const getLabels = () => {
  try {
    return { ...DEFAULT_LABELS, ...JSON.parse(localStorage.getItem(LABEL_KEY) || "{}") };
  } catch {
    return { ...DEFAULT_LABELS };
  }
};

// every colour the user has created so far, in order
export const getPalette = () => {
  const extra = new Set();
  Object.keys(getLabels()).forEach((c) => !FLAG_COLORS[c] && extra.add(c));
  getFlags().forEach((f) => !FLAG_COLORS[f.color] && extra.add(f.color));
  return [...FLAG_ORDER, ...[...extra].sort((a, b) => flagRank(a) - flagRank(b))];
};

// a tune can carry flags on several versions — surface a single one, by colour order
export const topFlagForTune = (tuneId) => {
  const list = flagsForTune(tuneId);
  const order = getPalette();
  for (const c of order) {
    const hit = list.find((f) => f.color === c);
    if (hit) return hit;
  }
  return list[0] || null;
};

export const setLabel = (color, text) => {
  const labels = { ...getLabels(), [color]: text };
  localStorage.setItem(LABEL_KEY, JSON.stringify(labels));
  window.dispatchEvent(new Event("flag-changed"));
  if (backendEnabled) flagsApi.setLabels(labels).catch(() => {});
};

let backendEnabled = false;
export const setFlagSync = (v) => {
  backendEnabled = v;
};

// Cycles through the colours the user already has; premium members get a brand
// new colour at the end of the round (up to 50), everyone else goes back to none.
export const cycleFlag = (flag, { premium = false } = {}) => {
  const list = getFlags();
  const current = list.find((f) => f.key === flag.key);
  const palette = getPalette();
  const rest = list.filter((f) => f.key !== flag.key);

  let next = null;
  if (!current) {
    next = palette[0];
  } else {
    const i = palette.indexOf(current.color);
    if (i > -1 && i < palette.length - 1) next = palette[i + 1];
    else if (premium && palette.length < MAX_FLAG_COLORS) {
      next = `c${flagRank(palette[palette.length - 1]) + 1}`;
      setLabel(next, `Colour ${palette.length + 1}`);
    }
  }

  if (next) {
    const entry = { ...flag, color: next };
    write([entry, ...rest]);
    if (backendEnabled) flagsApi.set(entry).catch(() => {});
    return next;
  }
  write(rest);
  if (backendEnabled) flagsApi.clear(flag.key).catch(() => {});
  return null;
};

// premium: add a brand-new colour to the palette straight from the Flags page
export const createFlagColor = () => {
  const palette = getPalette();
  if (palette.length >= MAX_FLAG_COLORS) return null;
  const next = `c${flagRank(palette[palette.length - 1]) + 1}`;
  setLabel(next, `Colour ${palette.length + 1}`);
  return next;
};

export const removeFlag = (key) => {
  if (backendEnabled) flagsApi.clear(key).catch(() => {});
  write(getFlags().filter((f) => f.key !== key));
};

// On sign-in: merge server flags with local ones, push local-only up.
export const syncFlagsFromBackend = async () => {
  const local = getFlags();
  let res;
  try {
    res = await flagsApi.list();
  } catch {
    return;
  }
  const server = res.flags || [];
  const serverKeys = new Set(server.map((f) => f.key));
  await Promise.all(
    local.filter((f) => !serverKeys.has(f.key)).map((f) => flagsApi.set(f).catch(() => {}))
  );
  const byKey = {};
  [...server, ...local].forEach((f) => {
    byKey[f.key] = { ...byKey[f.key], ...f };
  });
  write(Object.values(byKey));
  if (res.labels && Object.keys(res.labels).length) {
    localStorage.setItem(LABEL_KEY, JSON.stringify(res.labels));
  }
};

export const clearLocalFlags = () => write([]);
